import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash/noop';
import Button from '../button';
import VideoCard from '../video-card';
import _get from 'lodash/get';
import formatTime from '../utils/format-time';

/**
 * Generic Items list
 * @param {array} listItems An array of items to be listed
 * @param {boolean} useButton Needs a bottom button
 * @param {boolean} hideButton Should button be hide
 * @param {string} buttonText A text displayed in the list bottom button
 * @param {function} buttonClickHandler A handler for a button component
 * @param {sting} className
 */

const List = ({
    listItems,
    useButton,
    buttonText,
    hideButton,
    buttonClickHandler,
    onItemClick,
    videoCardType,
    itemsBasePath,
    topicSlug,
    currentPlayingClipId,
    focusIndex,
    lang,
    ...otherProps
}) => {

    const onAnchorClick = (event, itemData, index, slug) => {

        onItemClick(slug, event, itemData, index);

    };

    const getHyperlinkUrl= (itemSlug) => {

        const itemTopicSlug = topicSlug
            ? `?t=${topicSlug}`
            : '';

        return `${itemsBasePath}/${itemSlug}${itemTopicSlug}`;

    };

    const focusedItem = useRef();

    useEffect(() => {

        const elementRef = _get(focusedItem, 'current');
        elementRef && elementRef.focus();

    }, [focusIndex]);

    return (
        <div {...otherProps}>
            <ul className="list__items">
                {listItems.map((item, index) => {

                    const duration = _get(item, 'duration', '');
                    const blurb = _get(item, 'blurb');
                    const id = _get(item, 'id');
                    const date = _get(item, 'date');
                    const imageCuts = _get(item, 'image.cuts');
                    const imageAlt = _get(item, 'image.altText');
                    const slug = _get(item, 'slug');
                    const href = getHyperlinkUrl(slug);
                    const playing = id === currentPlayingClipId;
                    const aTagProps = {
                        'aria-label': blurb,
                        onClick: (e) => onAnchorClick(e, item, index, slug),
                        href,
                    };
                    const getFocus = index === focusIndex;
                    if (getFocus) {

                        aTagProps.ref = focusedItem;

                    }

                    return (
                        <li key={id} className="list__item">
                            <a {...aTagProps} className="list__item-link">
                                <VideoCard
                                    key={id}
                                    blurb={blurb}
                                    imageCuts={imageCuts}
                                    imageAlt={imageAlt}
                                    duration={formatTime(duration)}
                                    lastPublish={date}
                                    type={videoCardType}
                                    playing={playing}
                                    lang={lang}
                                />
                            </a>
                        </li>
                    );

                })}
            </ul>
            {(useButton && !hideButton)
                && <Button
                    text={buttonText}
                    className="list__button"
                    onClickHandler={buttonClickHandler}
                />}
        </div>
    );

};

List.propTypes = {
    listItems: PropTypes.arrayOf(PropTypes.object),
    useButton: PropTypes.bool,
    buttonText: PropTypes.string,
    hideButton: PropTypes.bool,
    buttonClickHandler: PropTypes.func,
    className: PropTypes.string,
    onItemClick: PropTypes.func,
    videoCardType: PropTypes.string,
    itemsBasePath: PropTypes.string.isRequired,
    topicSlug: PropTypes.string,
    currentPlayingClipId: PropTypes.string,
    focusIndex: PropTypes.number,
};

List.defaultProps = {
    listItems: [],
    useButton: true,
    buttonText: 'See More',
    hideButton: false,
    buttonClickHandler: _noop,
    className: '',
    videoCardType: VideoCard.types.PORTRAIT,
    currentPlayingClipId: '',
    onItemClick: _noop,
    focusIndex: null,
    lang: 'en',
};

export default List;
